import React, { useRef, useState } from 'react';
import { AlertCircle, CheckCircle2, ImagePlus, Loader2, Lock, Trash2, Upload } from 'lucide-react';
import { SMIIFY_API } from '../../../../config/api';
import styles from './LogoUploader.module.css';

// =============================================================================
// Branch logo: shown on invoices (screen, PDF, print, client page) and in WhatsApp link previews.
// The image is shrunk in the browser to 512 px (PNG keeps a transparent background), then saved
// in the database by POST /branding/logo. Access: settings.edit_profile or settings.edit.
// =============================================================================
const MAX_SIDE = 512;
const MAX_UPLOAD = 900 * 1024;
const ACCEPT = ['image/png', 'image/jpeg', 'image/webp'];

const readFile = (file) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error('Could not read the file.'));
    r.readAsDataURL(file);
});

const loadImage = (src) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('This file is not a valid image.'));
    img.src = src;
});

/** Shrink to MAX_SIDE; PNG first (transparency), JPEG on white if PNG is still too large */
const shrink = async (file) => {
    const img = await loadImage(await readFile(file));
    const ratio = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
    const w = Math.max(1, Math.round(img.width * ratio));
    const h = Math.max(1, Math.round(img.height * ratio));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, w, h);
    let dataUrl = canvas.toDataURL('image/png');
    if (dataUrl.length * 0.75 > MAX_UPLOAD) {
        ctx.globalCompositeOperation = 'destination-over';
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, w, h);
        dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    }
    return { dataUrl, width: w, height: h };
};

export default function LogoUploader({ storeId, logoUrl = '', canEdit = false, onChange }) {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState('');
    const [msg, setMsg] = useState(null);
    const [broken, setBroken] = useState(false);

    const flash = (type, text) => { setMsg({ type, text }); if (type === 'ok') setTimeout(() => setMsg(null), 3500); };
    const headers = () => ({
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('smiify_token')}`,
        ...(storeId ? { 'x-store-id': String(storeId) } : {}),
    });

    const pick = () => { if (canEdit && !busy) inputRef.current?.click(); };

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (!ACCEPT.includes(file.type)) { flash('error', 'Choose a PNG, JPG or WEBP image.'); return; }
        if (file.size > 8 * 1024 * 1024) { flash('error', 'Choose an image smaller than 8 MB.'); return; }
        setBusy('upload');
        setMsg(null);
        try {
            const { dataUrl, width, height } = await shrink(file);
            const res = await fetch(`${SMIIFY_API}/branding/logo`, { method: 'POST', headers: headers(), body: JSON.stringify({ storeId, dataUrl, width, height }) });
            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json.success) throw new Error(json.message || 'Could not save the logo.');
            setBroken(false);
            onChange?.(json.data?.logo || '');
            flash('ok', 'Logo saved. It now shows on bills and WhatsApp previews.');
        } catch (err) {
            flash('error', err.message || 'Could not save the logo.');
        } finally {
            setBusy('');
        }
    };

    const remove = async () => {
        if (!canEdit || busy || !window.confirm('Remove the logo from bills and previews?')) return;
        setBusy('remove');
        setMsg(null);
        try {
            const res = await fetch(`${SMIIFY_API}/branding/logo?storeId=${encodeURIComponent(storeId)}`, { method: 'DELETE', headers: headers() });
            const json = await res.json().catch(() => ({}));
            if (!res.ok || !json.success) throw new Error(json.message || 'Could not remove the logo.');
            onChange?.('');
            flash('ok', 'Logo removed.');
        } catch (err) {
            flash('error', err.message || 'Could not remove the logo.');
        } finally {
            setBusy('');
        }
    };

    const hasLogo = Boolean(logoUrl) && !broken;

    return (
        <section className={styles.card} aria-label="Salon logo">
            <div className={`${styles.box} ${hasLogo ? '' : styles.boxEmpty}`} onClick={pick} role={canEdit ? 'button' : undefined} tabIndex={canEdit ? 0 : -1}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(); } }} title={canEdit ? 'Upload logo' : undefined}>
                {busy === 'upload' ? <Loader2 size={22} className={styles.spin} />
                    : hasLogo ? <img src={logoUrl} alt="Salon logo" onError={() => setBroken(true)} />
                        : <ImagePlus size={24} />}
            </div>

            <div className={styles.body}>
                <strong>Salon logo</strong>
                <small>Shown on bills, PDF, print, the client's bill page and WhatsApp link previews. Square PNG with a clear background looks best.</small>

                {canEdit ? (
                    <div className={styles.actions}>
                        <button type="button" className={styles.primary} onClick={pick} disabled={Boolean(busy)}>
                            <Upload size={13} /> {hasLogo ? 'Change logo' : 'Upload logo'}
                        </button>
                        {hasLogo && (
                            <button type="button" className={styles.ghost} onClick={remove} disabled={Boolean(busy)}>
                                {busy === 'remove' ? <Loader2 size={13} className={styles.spin} /> : <Trash2 size={13} />} Remove
                            </button>
                        )}
                    </div>
                ) : (
                    <span className={styles.locked}><Lock size={11} /> Ask an admin to change the logo.</span>
                )}

                {msg && (
                    <span className={msg.type === 'ok' ? styles.ok : styles.err} role="status">
                        {msg.type === 'ok' ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />} {msg.text}
                    </span>
                )}
            </div>

            <input ref={inputRef} type="file" accept={ACCEPT.join(',')} hidden onChange={onFile} />
        </section>
    );
}